import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Driver } from '@hero/schema';
import { SharedTable, type StatusBadgeConfigType } from '../../../app/Table/SharedTable';
import type { SearchInputProps } from '../../../app/Search/SearchInput';
import { DriverRow } from './DriverRow';

export interface DriverStatusFilterConfig {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  options: Array<{
    value: string;
    label: string;
    count?: number;
    className?: string;
  }>;
  counts?: Array<{ value: string; count: number }>;
}

export interface DriverTableFilters {
  showSearch?: boolean;
  statusFilter?: DriverStatusFilterConfig;
}

export interface DriverTableProps {
  drivers: Driver[];
  isLoading?: boolean;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  searchProps?: SearchInputProps;
  onSort?: (column: string) => void;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  filters?: DriverTableFilters;
  /** Empty-state shown only when the list is empty AND no filters/search are active. */
  emptyState?: React.ReactNode;
  /** Set to `true` whenever the user has search / filter / sort applied. */
  hasActiveFilters?: boolean;
  children?: React.ReactNode;
}

const DRIVER_STATUS_BADGE_CONFIG: StatusBadgeConfigType = {
  getStatusClass: (status: string) =>
    status === 'active' ? 'bg-success-50 text-success-400' : 'bg-gray-100 text-gray-600',
  getStatusDisplayText: (status: string) => status,
};

const DriverTableComponent: React.FC<DriverTableProps> = ({
  drivers,
  isLoading,
  sortColumn,
  sortDirection = 'asc',
  searchProps,
  onSort,
  onLoadMore = () => {},
  hasNextPage = false,
  isFetchingNextPage = false,
  filters,
  emptyState,
  hasActiveFilters,
  children,
}) => {
  const { t } = useTranslation(['m-driver']);

  const columns = useMemo(
    () => [
      { key: 'driver', label: t('table.columns.driver'), sortable: false },
      { key: 'status', label: t('table.columns.status'), sortable: false },
      { key: 'vehicle', label: t('table.columns.vehicle'), sortable: false },
      { key: 'actions', label: '', sortable: false },
    ],
    [t],
  );

  return (
    <SharedTable
      data={drivers}
      isLoading={isLoading}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      searchProps={searchProps}
      onSort={onSort}
      statusBadgeConfig={DRIVER_STATUS_BADGE_CONFIG}
      onLoadMore={onLoadMore}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      emptyState={emptyState}
      hasActiveFilters={hasActiveFilters}
      getRowId={(driver: Driver) => driver._id.toString()}>
      {children || <DriverTableHead filters={filters} />}

      <SharedTable.Mid>
        <SharedTable.Rows
          columns={columns}
          rowRender={(driver: Driver) => <DriverRow driver={driver} />}
        />
      </SharedTable.Mid>
    </SharedTable>
  );
};

const DriverTableHead: React.FC<{ filters?: DriverTableFilters }> = ({ filters }) => {
  if (!filters) return null;

  return (
    <SharedTable.Head>
      {filters.showSearch !== false && <SharedTable.Search />}
      {filters.statusFilter && (
        <SharedTable.Badge
          options={filters.statusFilter.options}
          counts={filters.statusFilter.counts}
          value={filters.statusFilter.value}
          onChange={filters.statusFilter.onChange}
          showCounts={!!filters.statusFilter.counts}
        />
      )}
    </SharedTable.Head>
  );
};

export const DriverTable = Object.assign(DriverTableComponent, {
  Head: SharedTable.Head,
  Search: SharedTable.Search,
  Badge: SharedTable.Badge,
  Mid: SharedTable.Mid,
  Rows: SharedTable.Rows,
});
